const RESPONSE_TARGET_KEYS = {
  Player: "player",
  Team: "tournamentTeam",
  TournamentTeam: "tournamentTeam",
  Sponsor: "sponsor",
  Order: "registrationOrder",
  RegistrationOrder: "registrationOrder",
};

const RESPONSE_SCOPES = {
  Player: "player",
  Team: "team",
  TournamentTeam: "team",
  Sponsor: "sponsor",
  Order: "order",
  RegistrationOrder: "order",
};

export const buildResponseTarget = (record) => {
  const key = RESPONSE_TARGET_KEYS[record?.type];
  if (!key || !record?.id) {
    return {};
  }
  return { [key]: { id: record.id } };
};

const getQuestionFieldSet = (question) => question?.registrationFieldSet;

export const SCOPE_SUBTITLES = {
  order: "Order Responses",
  team: "Team Responses",
  player: "Player Responses",
  sponsor: "Sponsor Responses",
};

export const toSelectOption = (option) => {
  if (option && typeof option === "object") {
    const value = option.value ?? option.id ?? option.key ?? option.name ?? "";
    const label = option.label ?? option.name ?? option.value ?? `${value}`;
    return { label: `${label}`, value };
  }
  return { label: `${option}`, value: option };
};

const RESPONSE_TYPE_OPTIONS = [
  { key: "all", label: "All", scope: null },
  { key: "order", label: "Order", scope: "order" },
  { key: "sponsor", label: "Sponsor", scope: "sponsor" },
  { key: "team", label: "Team", scope: "team" },
  { key: "player", label: "Players", scope: "player" },
];

export const RESPONSE_TYPE_LABELS = {
  all: "All Responses",
  order: "Order Responses",
  sponsor: "Sponsor Responses",
  team: "Team Responses",
  player: "Player Responses",
};

export const getAvailableResponseTypeOptions = (payload) => {
  const scopes = new Set(
    getResponseNodes(payload).flatMap((node) =>
      (node?.forms ?? []).map((form) => form?.scope)
    )
  );
  return RESPONSE_TYPE_OPTIONS.filter(
    (option) => option.scope === null || scopes.has(option.scope)
  );
};

const includesTerm = (value, term) =>
  (value ?? "").toString().toLowerCase().includes(term);

const questionMatchesSearch = (question, term) => {
  if (!term) {
    return true;
  }
  if (includesTerm(question?.question?.name, term)) {
    return true;
  }
  return (question?.responses ?? []).some(
    (answer) =>
      includesTerm(answer?.response?.value, term) ||
      includesTerm(answer?.record?.name, term)
  );
};

const teamRecordTypes = ["Team", "TournamentTeam"];

export const filterResponseNodes = (nodes, options = {}) => {
  const {
    scope = "all",
    search = "",
    player = null,
    team = null,
    sponsor = null,
  } = options;
  const term = search.trim().toLowerCase();
  const scopeValue = scope === "all" ? null : scope;
  return (nodes ?? [])
    .filter((node) => {
      if (team != null) {
        return (
          teamRecordTypes.includes(node?.record?.type) &&
          `${node?.record?.id}` === `${team}`
        );
      }
      if (sponsor != null) {
        return (
          node?.record?.type === "Sponsor" &&
          `${node?.record?.id}` === `${sponsor}`
        );
      }
      return true;
    })
    .map((node) => ({
      ...node,
      forms: (node?.forms ?? [])
        .filter((form) => !scopeValue || form?.scope === scopeValue)
        .map((form) => {
          let questions = form?.questions ?? [];
          if (player != null) {
            questions = questions
              .map((question) => ({
                ...question,
                responses: (question?.responses ?? []).filter(
                  (answer) => `${answer?.record?.id}` === `${player}`
                ),
              }))
              .filter((question) => question.responses.length > 0);
          }
          questions = questions.filter((question) =>
            questionMatchesSearch(question, term)
          );
          return { ...form, questions };
        })
        .filter((form) => form.questions.length > 0),
    }))
    .filter((node) => node.forms.length > 0);
};

export const getResponsePlayers = (payload) => {
  const players = new Map();
  getResponseNodes(payload).forEach((node) => {
    (node?.forms ?? []).forEach((form) => {
      (form?.questions ?? []).forEach((question) => {
        (question?.responses ?? []).forEach((answer) => {
          const rec = answer?.record;
          if (rec?.type === "Player" && rec?.id != null && !players.has(rec.id)) {
            players.set(rec.id, { id: rec.id, name: rec.name });
          }
        });
      });
    });
  });
  return [...players.values()];
};

const getNodesByType = (payload, matches) => {
  const items = new Map();
  getResponseNodes(payload).forEach((node) => {
    const record = node?.record;
    if (matches(record?.type) && record?.id != null && !items.has(record.id)) {
      items.set(record.id, { id: record.id, name: record.name });
    }
  });
  return [...items.values()];
};

export const getResponseTeams = (payload) =>
  getNodesByType(payload, (type) => teamRecordTypes.includes(type));

export const getResponseSponsors = (payload) =>
  getNodesByType(payload, (type) => type === "Sponsor");

const formHasResponseRows = (form) =>
  (form?.questions ?? []).some((question) =>
    (question?.responses ?? []).some((answer) => answer?.response != null)
  );

export const getResponseNodes = (payload) => {
  if (!payload) {
    return [];
  }
  const orderNode = {
    record: payload.record ?? {},
    package: payload.package,
    forms: (payload.forms ?? []).filter(formHasResponseRows),
  };
  const registrations = (payload.registrations ?? []).map((registration) => ({
    record: registration?.record ?? {},
    package: registration?.package,
    forms: (registration?.forms ?? []).filter(formHasResponseRows),
  }));
  return [orderNode, ...registrations];
};

export const getRecordResponseGroup = (payload) => {
  if (!payload?.record) {
    return null;
  }
  const { record } = payload;
  const scope = RESPONSE_SCOPES[record.type] ?? "player";
  const formsByKey = new Map();
  (payload.items ?? []).forEach((item) => {
    const fieldSet = getQuestionFieldSet(item?.question);
    const key = fieldSet
      ? `field-set-${fieldSet.id ?? fieldSet.name}`
      : `record-${record.id}`;
    if (!formsByKey.has(key)) {
      formsByKey.set(key, {
        form: { id: key, fieldSetKey: fieldSet?.id, name: fieldSet?.name ?? record.name },
        scope,
        questions: [],
      });
    }
    formsByKey.get(key).questions.push({
      question: item?.question,
      responses: [{ record, response: item?.response ?? null }],
    });
  });
  return {
    record,
    package: payload.package,
    forms: [...formsByKey.values()].filter(formHasResponseRows),
  };
};

export const isAnswered = (answer) => answer?.response?.value != null;

export const isFormOrderTied = (form) =>
  (form?.questions ?? []).some((question) =>
    (question?.responses ?? []).some(
      (answer) => answer?.response?.registrationOrderKey != null
    )
  );

const isBlankRow = (answer) =>
  answer?.response != null && `${answer.response.value ?? ""}`.trim() === "";

const isFormComplete = (form) =>
  !(form?.questions ?? [])
    .flatMap((question) => question?.responses ?? [])
    .some(isBlankRow);

export const getOrderForms = (payload) =>
  getResponseNodes(payload).flatMap((node) =>
    (node.forms ?? []).filter(Boolean).map((form) => ({
      record: node.record,
      form: form?.form,
      scope: form?.scope,
      questions: form?.questions ?? [],
      complete: isFormComplete(form),
    }))
  );
